/*=========================================================================================
  File Name: main.js
  Description: main vue(js) file
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
Vue.prototype._ = window._;
import App from "./App.vue";
import router from "./router";
import store from "./store/store";
import i18n from "./i18n/i18n";
import acl from "./auth/acl";
import axios from "./axios.js";

import "./http/requests";
import "../themeConfig.js";
import "./globalComponents.js"; // Globally Registered Components
import "./utils/filters"; // Vuexy Admin Filters
import "@/firebase/firebaseConfig";
import DOMPurify from "dompurify";

import TextInput from "@/components/basic-elements/TextInput";
import Icon from "@/components/basic-elements/Icon";

Vue.component("TextInput", TextInput);
// eslint-disable-next-line vue/multi-word-component-names
Vue.component("Icon", Icon);

// Vuesax Component Framework
import Vuesax from "vuesax";
Vue.use(Vuesax);

// Auth0 Plugin
import AuthPlugin from "./plugins/auth";
Vue.use(AuthPlugin);

import VueFullscreen from "vue-fullscreen";
Vue.use(VueFullscreen);

// Clipboard
import VueClipboard from "vue-clipboard2";
Vue.use(VueClipboard);

// VeeValidate
import VeeValidate from "vee-validate";
Vue.use(VeeValidate, { fieldsBagName: "veeFields" });

// Vuejs - Vue wrapper for hammerjs
import { VueHammer } from "vue2-hammer";
Vue.use(VueHammer);

import EventBus from "./EventBus";
Vue.use(EventBus);

// Feather font icon (CSS moved to app.js)
import VueRadioToggleButtons from "vue-radio-toggle-buttons";
Vue.use(VueRadioToggleButtons);

import VuePerfectScrollbar from "vue-perfect-scrollbar";
Vue.component("VuePerfectScrollbar", VuePerfectScrollbar);

import vue2Dropzone from "vue2-dropzone";
Vue.component("vue-dropzone", vue2Dropzone);

// import Laravel Permission To VueJS
import LaravelPermissionToVueJS from "./auth/jsPermissions";
Vue.use(LaravelPermissionToVueJS);
import { can } from "./auth/permission.js";
import { is } from "./auth/roles";
import "./utils/validations/validations";
Vue.prototype.$permission = { can };
Vue.prototype.$roles = { is };

Vue.directive("initial-html", {
  // eslint-disable-next-line no-unused-vars
  inserted(el, binding, vnode) {
    el.innerHTML = binding.value;
  },
});

Vue.directive("sane-html", (el, binding) => {
  el.innerHTML = DOMPurify.sanitize(binding.value);
});

// navigation guards before each
router.beforeEach((to, from, next) => {
  const notRequiresAuth = [
    "/",
    "/login",
    "/register",
    "/forgot-password",
    "/reset-password",
  ];
  const logged = JSON.parse(localStorage.getItem("userInfo"));
  const expiresAt = localStorage.getItem("expiresAt");
  const IsRequiresAuth = !notRequiresAuth.includes(to.path);

  // Routes that don't require authentication
  if (!IsRequiresAuth) {
    return next();
  }

  // User not logged in
  if (!logged) {
    return next({ path: "/login" });
  }

  // Check if token expired
  if (new Date(expiresAt).getTime() < new Date().getTime()) {
    return next({ path: "/login" });
  }

  // Check role and permission
  let initialRole = "student";

  if (logged.role) {
    initialRole = logged.role.name;
  }

  let allow = false;

  // 1. Check by role
  if (to.meta.role) {
    if (to.meta.role.includes("*") || to.meta.role.includes(initialRole)) {
      allow = true;
    }
  }

  // 2. Check by permission (only if role check failed)
  if (allow === false && to.meta.permission) {
    const perm = to.meta.permission;
    // ["*"] means any logged-in user is allowed
    if (Array.isArray(perm) && perm.includes("*")) {
      allow = true;
    } else if (typeof perm === "string" && can(perm)) {
      allow = true;
    }
  }

  // 3. Routes with no meta restrictions are open
  if (!to.meta || (!to.meta.role && !to.meta.permission)) {
    allow = true;
  }

  // 4. Handle not-allowed
  if (allow === false) {
    if (to.path === "/dashboard") {
      return next({ path: "/meetings" });
    }
    return next({ path: "/pages/error-404" });
  }

  return next();
});

Vue.config.productionTip = false;

Vue.prototype.$pageOptions = [10, 50, 100];
Vue.prototype.$perPage = 10;
Vue.prototype.$cityOptions = [];
Vue.prototype.$http = axios;

import { VueQueryPlugin } from "@tanstack/vue-query";
import { queryClient } from "@/api/_core/queryClient";
Vue.use(VueQueryPlugin, { queryClient });

new Vue({
  router,
  store,
  i18n,
  acl,
  render: (h) => h(App),
}).$mount("#app");
